import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 20;

function PhotoGrid({ title, description, photos }) {
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(photos.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return photos.slice(start, start + PAGE_SIZE);
  }, [photos, safePage]);

  useEffect(() => setPage(1), [photos]);
  useEffect(() => {
    if (!selected) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selected]);

  const hasPrev = safePage > 1;
  const hasNext = safePage < totalPages;
  const goPrev = useCallback(() => setPage((current) => Math.max(1, current - 1)), []);
  const goNext = useCallback(() => setPage((current) => Math.min(totalPages, current + 1)), [totalPages]);

  return (
    <div className="gallery-page">
      <h1>{title}</h1>
      <p>{description}</p>
      {pageItems.length === 0 ? (
        <div className="no-media"><p>No photos found.</p></div>
      ) : (
        <>
          <div className="photos-grid">
            {pageItems.map((photo) => (
              <button
                key={photo.url}
                type="button"
                className="photo-card"
                onClick={() => setSelected(photo)}
                aria-label={`Preview ${photo.filename || "photo"}`}
              >
                <img
                  src={photo.url}
                  alt={photo.filename || photo.url.split("/").pop()}
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.alt = "Image not found";
                    event.currentTarget.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(
                      "<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='100%' height='100%' fill='#eee5d3'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#6f5a3b' font-family='sans-serif' font-size='14'>Image not found</text></svg>",
                    );
                  }}
                />
                <span className="photo-info">
                  <span className="filename">{photo.filename || photo.url.split("/").pop()}</span>
                </span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="photo-preview-modal" onClick={() => setSelected(null)}>
              <div className="photo-preview-content" onClick={(event) => event.stopPropagation()}>
                <button className="close-button" onClick={() => setSelected(null)} aria-label="Close photo preview">×</button>
                <img className="photo-preview-image" src={selected.url} alt={selected.filename || "Selected photo"} />
                <div className="preview-info"><h3>{selected.filename || selected.url.split("/").pop()}</h3></div>
              </div>
            </div>
          )}

          <div className="pagination">
            <button onClick={goPrev} disabled={!hasPrev}>Previous</button>
            <span className="page-info">Page {safePage} of {totalPages}</span>
            <button onClick={goNext} disabled={!hasNext}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}

function usePhotoCollection(endpoint) {
  const [state, setState] = useState({ loading: true, locked: false, error: "", photos: [] });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await fetch(endpoint, { headers: { Accept: "application/json" }, cache: "no-store" });
      if (response.status === 401) {
        setState({ loading: false, locked: true, error: "", photos: [] });
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setState({ loading: false, locked: false, error: "", photos: Array.isArray(data.photos) ? data.photos : [] });
    } catch (error) {
      console.error("Error loading photos:", error);
      setState({ loading: false, locked: false, error: "The photo collection could not be loaded.", photos: [] });
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export function PublicPhotos() {
  const { loading, error, photos } = usePhotoCollection("/media/index.json");
  if (loading) return <div className="loading">Loading public photos…</div>;
  if (error) return <div className="error">{error}</div>;
  return <PhotoGrid title="Public Photos" description="Photographs available to all visitors. Click any photo to preview it." photos={photos} />;
}

export function PrivatePhotos() {
  const { loading, locked, error, photos, reload } = usePhotoCollection("/api/private-photos");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const unlock = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setPasswordError("");
    try {
      const response = await fetch("/api/private-about/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        setPasswordError("That password is not correct. Please try again.");
        return;
      }
      setPassword("");
      await reload();
    } catch {
      setPasswordError("The private photos could not be unlocked. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading private photos…</div>;
  if (error) return <div className="error">{error}</div>;
  if (locked) {
    return (
      <section className="about-lock" role="main" aria-labelledby="private-photos-title">
        <div className="about-lock__card">
          <div className="about-lock__cross" aria-hidden="true">🔒</div>
          <h1 id="private-photos-title">Private Photos</h1>
          <p>Enter the family password to view the private photographs.</p>
          <form onSubmit={unlock} className="about-lock__form">
            <label htmlFor="private-photo-password">Enter password to continue</label>
            <input
              id="private-photo-password"
              type="password"
              value={password}
              onChange={(event) => { setPassword(event.target.value); setPasswordError(""); }}
              autoComplete="off"
              autoFocus
            />
            {passwordError && <p className="about-lock__error" role="alert">{passwordError}</p>}
            <button type="submit" disabled={submitting}>{submitting ? "Checking…" : "View private photos"}</button>
          </form>
        </div>
      </section>
    );
  }

  return <PhotoGrid title="Private Photos" description="Password-protected family photographs." photos={photos} />;
}

export default function GalleryPhotos() {
  const navigate = useNavigate();
  return (
    <div className="gallery-page">
      <h1>Photos Gallery</h1>
      <p>Choose a photo collection.</p>
      <div className="video-library-choices">
        <button type="button" className="video-library-card" onClick={() => navigate("/gallery/photos/public")}>
          <span className="video-library-card__icon" aria-hidden="true">🌐</span>
          <strong>Public Photos</strong>
          <span>Open photographs available to everyone</span>
        </button>
        <button type="button" className="video-library-card" onClick={() => navigate("/gallery/photos/private")}>
          <span className="video-library-card__icon" aria-hidden="true">🔒</span>
          <strong>Private Photos</strong>
          <span>Password-protected family collection</span>
        </button>
      </div>
    </div>
  );
}
